import React, { useState, useRef, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import ls from 'localstorage-slim';
import { Icon } from '@rsuite/icons';
import { Pagination, Dropdown } from 'rsuite';
import {
  FaRegTimesCircle,
  FaRegCheckCircle,
  FaRegHourglass,
  FaRegFileExcel,
  FaArrowAltCircleDown,
  FaArrowAltCircleUp,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import Loading from '../GeneralComponents/Loading';
import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';
import DataContext from '../context/InvoiceData';

function SearchPortalDocument() {
  let navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const subDomain = ls.get('subDomain');

  const { setStep, setDocumentType, setDocumentTypeVersion } = useContext(DataContext);

  const [isLoading, setIsLoading] = useState(false);
  const [isBasic, setIsBasic] = useState(true);
  const [queryParameters, setQueryParameters] = useState({});
  const [searchedDocuments, setSearchedDocuments] = useState(null);
  const [continuationToken, setcontinuationToken] = useState(null);
  const [currentTaxpayer, setCurrentTaxpayer] = useState(null);

  const refForm = useRef(null);

  const minIssueToDate = moment(queryParameters?.issueDateFrom, true)
    .utc()
    .format('YYYY-MM-DDTHH:mm');
  const maxIssueToDate = moment(queryParameters?.issueDateFrom, true)
    .utc()
    .add(31, 'days')
    .format('YYYY-MM-DDTHH:mm');
  const minSubmitToDate = moment(queryParameters?.submissionDateFrom, true)
    .utc()
    .format('YYYY-MM-DDTHH:mm');
  const maxSubmitToDate = moment(queryParameters?.submissionDateFrom, true)
    .utc()
    .add(31, 'days')
    .format('YYYY-MM-DDTHH:mm');

  const handleChange = event => {
    setQueryParameters({ ...queryParameters, [event.target.name]: event.target.value });
  };

  const handleIssueDateFrom = event => {
    const issueFrom = new Date(event.target.value).toISOString();
    setQueryParameters({ ...queryParameters, [event.target.name]: issueFrom });
  };

  const handleChangeInputType = (event, inputType) => {
    if (!event.target.value) event.target.type = inputType;
  };

  const handleIssueDateTo = event => {
    const issueTo = new Date(event.target.value).toISOString();
    setQueryParameters({ ...queryParameters, [event.target.name]: issueTo });
  };

  const handleSubmissionDateFrom = event => {
    const submitFrom = new Date(event.target.value).toISOString();
    setQueryParameters({ ...queryParameters, [event.target.name]: submitFrom });
  };

  const handleSubmissionDateTo = event => {
    const submitTo = new Date(event.target.value).toISOString();
    setQueryParameters({ ...queryParameters, [event.target.name]: submitTo });
  };

  const handleCopyInvoice = (uuid, type, version) => {
    axios
      .get(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/copy_invoice`, {
        params: {
          uuid: uuid,
        },
      })
      .then(response => {
        setStep(0);
        setDocumentType(type === 'i' ? 'Invoice' : type === 'c' ? 'Credit Note' : 'Debit Note');
        setDocumentTypeVersion(version);
        ls.set('id', response.data);
        navigate('/submitInvoiceSteps');
      })
      .catch(error => {
        console.log(error);
      });
  };

  const handleSearchDocuments = async () => {
    try {
      refForm.current.reset();

      setIsBasic(false);
      setIsLoading(true);

      if (!ls.get('ETAToken')) await getETAToken();

      if (!ls.get('access')) {
        await refreshToken();
        await updateUserLastLogin();
      }

      let searchResponse = await axios.get(
        `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/search_ETA_documents`,
        {
          params: {
            accessToken: ls.get('ETAToken'),
            taxpayer: ls.get('taxpayer_id'),
            ...queryParameters,
          },
        }
      );

      setSearchedDocuments(searchResponse.data.data.result);
      searchResponse.data.data.metadata['continuationToken'] !== 'EndofResultSet'
        ? setcontinuationToken(searchResponse.data.data.metadata['continuationToken'])
        : setcontinuationToken(null);
      setCurrentTaxpayer(searchResponse.data.rin);
      setIsLoading(false);
      toast.success("Documents' search is successful", {
        type: 'success',
        isLoading: false,
        autoClose: 1000,
        theme: 'colored',
      });
    } catch (error) {
      setQueryParameters({});
      setIsLoading(false);
      setIsBasic(true);
      if (error.response) {
        // Request made but the server responded with an error
        toast.error(`${error.response.data}`, {
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      } else if (error.request) {
        // Request made but no response is received from the server.
        toast.error(`${error.request}`, {
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      } else {
        // Error occured while setting up the request
        toast.error(`${error.message}`, {
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      }
    }
  };

  const handleBackToSearch = () => {
    setIsBasic(true);
    setQueryParameters({});
  };

  return isBasic ? (
    <div className='h-100'>
      {/* modal */}
      <div
        className='modal fade'
        id='searchModal'
        data-bs-backdrop='static'
        data-bs-keyboard='false'
        tabIndex='-1'
        aria-labelledby='searchModalLabel'
        aria-hidden='true'>
        <div className='modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title' id='searchModalLabel'>
                {t('document.etaSearch.button')}
              </h5>
              <button
                type='button'
                className='btn-close'
                data-bs-dismiss='modal'
                aria-label='Close'></button>
            </div>
            <div className='modal-body'>
              <form
                ref={refForm}
                className={`${i18n.language === 'en' ? 'pe-3' : 'ps-3'} text-center`}
                dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
                <div className='row mb-5'>
                  <label
                    htmlFor='direction'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block`}>
                    {t('document.etaSearch.modal.direction')}
                  </label>
                  <select
                    className='col-8 py-1'
                    id='direction'
                    name='direction'
                    dir={i18n.language === 'en' ? 'ltr' : 'rtl'}
                    onChange={handleChange}>
                    <option defaultValue={''}></option>
                    <option value='Sent'>{t('document.sobolSearch.modal.options.submit')}</option>
                    <option value='Received'>
                      {t('document.sobolSearch.modal.options.received')}
                    </option>
                  </select>
                </div>
                <div className='row mb-5'>
                  <label
                    htmlFor='documentType'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block`}>
                    {t('document.etaSearch.modal.type')}
                  </label>
                  <select
                    className='col-8 py-1'
                    aria-label='Default select example'
                    id='documentType'
                    name='documentType'
                    onChange={handleChange}>
                    <option defaultValue={''}></option>
                    <option value='i'>{t('document.documentDetails.invoice')}</option>
                    <option value='c'>{t('document.documentDetails.credit')}</option>
                    <option value='d'>{t('document.documentDetails.debit')}</option>
                  </select>
                </div>
                <div className='row mb-5'>
                  <label
                    htmlFor='status'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block`}>
                    {t('document.etaSearch.modal.status')}
                  </label>
                  <select
                    className='col-8 py-1'
                    aria-label='Default select example'
                    id='status'
                    name='status'
                    onChange={handleChange}>
                    <option defaultValue={''}></option>
                    <option value='Submitted'>
                      {t('document.sobolSearch.modal.options.submitted')}
                    </option>
                    <option value='Valid'>{t('document.sobolSearch.modal.options.valid')}</option>
                    <option value='Invalid'>
                      {t('document.sobolSearch.modal.options.invalid')}
                    </option>
                    <option value='Rejected'>
                      {t('document.sobolSearch.modal.options.rejected')}
                    </option>
                    <option value='Cancelled'>
                      {t('document.sobolSearch.modal.options.cancelled')}
                    </option>
                  </select>
                </div>
                <div className='row mb-5 align-items-baseline'>
                  <label
                    htmlFor='issueDateFrom'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block text-nowrap`}>
                    {t('document.etaSearch.modal.issue')}
                  </label>
                  <input
                    type='text'
                    className={`${i18n.language === 'en' ? 'me-1' : 'ms-1'} col py-1`}
                    id='issueDateFrom'
                    name='issueDateFrom'
                    onChange={handleIssueDateFrom}
                    onFocus={event => handleChangeInputType(event, 'datetime-local')}
                    onBlur={event => handleChangeInputType(event, 'text')}
                    placeholder={t('document.from')}
                  />
                  <input
                    type='text'
                    className='col py-1'
                    id='issueDateTo'
                    name='issueDateTo'
                    min={minIssueToDate}
                    max={maxIssueToDate}
                    onChange={handleIssueDateTo}
                    onFocus={event => handleChangeInputType(event, 'datetime-local')}
                    onBlur={event => handleChangeInputType(event, 'text')}
                    placeholder={t('document.to')}
                    required={true}
                  />
                </div>
                <div className='row mb-2 align-items-baseline'>
                  <label
                    htmlFor='submissionDateFrom'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block text-nowrap`}>
                    {t('document.etaSearch.modal.submission')}
                  </label>
                  <input
                    type='text'
                    className={`${i18n.language === 'en' ? 'me-1' : 'ms-1'} col py-1`}
                    id='submissionDateFrom'
                    name='submissionDateFrom'
                    onChange={handleSubmissionDateFrom}
                    onFocus={event => handleChangeInputType(event, 'datetime-local')}
                    onBlur={event => handleChangeInputType(event, 'text')}
                    placeholder={t('document.from')}
                  />
                  <input
                    type='text'
                    className='col py-1'
                    id='submissionDateTo'
                    name='submissionDateTo'
                    min={minSubmitToDate}
                    max={maxSubmitToDate}
                    onChange={handleSubmissionDateTo}
                    onFocus={event => handleChangeInputType(event, 'datetime-local')}
                    onBlur={event => handleChangeInputType(event, 'text')}
                    required={queryParameters?.submissionDateFrom}
                    placeholder={t('document.to')}
                  />
                </div>
              </form>
            </div>
            <div className='modal-footer'>
              <button
                type='button'
                className='btn btn-success'
                data-bs-dismiss='modal'
                onClick={handleSearchDocuments}
                disabled={Object.values(queryParameters)?.filter(value => !!value).length < 2}>
                {t('document.etaSearch.button')}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Basic Page */}
      <div className='container'>
        <div className='row justify-content-center align-items-center'>
          <div className='row m-5 text-center justify-content-center align-items-center'>
            <img
              src='/Sobol_empty-search.png'
              alt='Sobol Logo'
              className='mx-auto my-lg-3 my-1 align-self-center'
              style={{ width: '30%' }}
            />
            <p className={`${i18n.language === 'en' ? '' : 'arabic'} fs-5 my-lg-2 my-sm-1`}>
              <strong>{t('document.etaSearch.hint')}</strong> {t('document.etaSearch.details')}
              <p className={`${i18n.language === 'en' ? '' : 'arabic'} fs-5 my-lg-2 my-sm-1`}>
                {t('document.etaSearch.required')}
              </p>
            </p>
            <div
              className='btn btn-sm btn-primary col-2 align-self-center mt-lg-3 mt-2'
              data-bs-toggle='modal'
              data-bs-target='#searchModal'>
              {t('document.etaSearch.button')}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : isLoading ? (
    <div style={{ height: '77vh' }}>
      <Loading />
    </div>
  ) : (
    <>
      <div className='container-fluid'>
        <div className='row justify-content-center mx-2 '>
          <div className='row my-1 justify-content-end'>
            <div className='btn btn-success col-2' onClick={handleBackToSearch}>
              {t('document.etaSearch.again')}
            </div>
          </div>
          <table className='table col mt-2' dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
            <thead
              style={{
                verticalAlign: 'middle',
              }}>
              <tr className='text-center'>
                <th className='col'></th>
                <th className='col-2'>{t('document.table.id')}</th>
                <th className='col'>{t('document.table.dateTime')}</th>
                <th className='col'>{t('document.table.type')}</th>
                <th className='col'>{t('document.table.total')}</th>
                <th className='col'>{t('document.table.issuer')}</th>
                <th className='col-2'>{t('document.table.receiver')}</th>
                <th className='col-2'>{t('document.table.submission')}</th>
                <th className='col-1'>{t('document.table.status')}</th>
                <th className='col'></th>
              </tr>
            </thead>
            <tbody>
              {searchedDocuments.map(invoice => (
                <tr key={invoice.longId}>
                  <td className='text-center'>
                    {currentTaxpayer === invoice.issuerId ? (
                      <Icon as={FaArrowAltCircleUp} color='black' className='me-1' />
                    ) : (
                      <Icon as={FaArrowAltCircleDown} color='blue' className='me-1' />
                    )}
                  </td>
                  <td className='text-center'>
                    <span
                      className='d-block fw-bold text-truncate'
                      style={{ maxWidth: '200px' }}
                      dir='ltr'>
                      <Link to={`../../documents/${invoice.uuid}`}>{invoice.uuid}</Link>
                    </span>
                    <span className='d-block'>{invoice.internalId}</span>
                  </td>
                  <td className='text-center'>
                    <span className='d-block fw-bold'>
                      {moment(invoice.dateTimeReceived).format('DD/MM/YYYY')}
                    </span>
                    <span className='d-block' dir='ltr'>
                      {moment(invoice.dateTimeReceived).format('h:mm a')}
                    </span>
                  </td>
                  <td className='text-center'>
                    <span className='d-block fw-bold'>
                      {invoice.typeName === 'i'
                        ? 'Invoice'
                        : invoice.typeName === 'c'
                        ? 'Credit Note'
                        : 'Debit Note'}
                    </span>
                    <span className='d-block'>{invoice.typeVersionName}</span>
                  </td>
                  <td className='text-center'>{invoice.total.toFixed(2)}</td>
                  <td className='text-center'>
                    <span
                      className='d-block fw-bold text-nowrap text-truncate'
                      style={{ maxWidth: '150px' }}
                      dir='ltr'>
                      {invoice.issuerName}
                    </span>
                    <span className='d-block'>{invoice.issuerId}</span>
                  </td>
                  <td className='text-center'>
                    <span
                      className='d-block fw-bold text-nowrap text-truncate'
                      style={{ maxWidth: '150px' }}
                      dir='ltr'>
                      {invoice.receiverName}
                    </span>
                    <span className='d-block'>{invoice.receiverId}</span>
                  </td>
                  <td className='text-truncate text-center' style={{ maxWidth: '150px' }} dir='ltr'>
                    {invoice.submissionUUID}
                  </td>
                  <td className='text-center'>
                    <span className='d-block'>
                      {invoice.status === 'Invalid' || invoice.status === 'Rejected' ? (
                        <Icon as={FaRegTimesCircle} color='red' />
                      ) : invoice.status === 'Valid' &&
                        !invoice.cancelRequestDate &&
                        !invoice.rejectRequestDate ? (
                        <Icon as={FaRegCheckCircle} color='green' />
                      ) : (invoice.cancelRequestDate &&
                          invoice.cancelRequestDelayedDate > new Date().toISOString()) ||
                        (invoice.rejectRequestDate &&
                          invoice.rejectRequestDelayedDate > new Date().toISOString()) ? (
                        <Icon as={FaRegHourglass} color='grey' />
                      ) : (
                        <Icon as={FaRegFileExcel} color='grey' />
                      )}
                    </span>
                    <span
                      className={`${
                        ['Invalid', 'Rejected'].includes(invoice.status)
                          ? 'text-danger'
                          : invoice.status === 'Valid'
                          ? 'text-success'
                          : 'text-muted'
                      }`}>
                      {invoice.cancelRequestDate &&
                      invoice.cancelRequestDelayedDate > new Date().toISOString()
                        ? `${invoice.status} \u2192 Cancelled`
                        : invoice.rejectRequestDate &&
                          invoice.rejectRequestDelayedDate > new Date().toISOString()
                        ? `${invoice.status} \u2192 Rejected`
                        : invoice.status}
                    </span>
                  </td>
                  <td className={`${invoice.isSobolIssued ? 'text-end' : 'd-none'}`} role='button'>
                    <Dropdown placement='bottomEnd' noCaret title='&#x22EE;'>
                      <Dropdown.Item
                        onClick={() => {
                          handleCopyInvoice(
                            invoice.uuid,
                            invoice.typeName,
                            invoice.typeVersionName
                          );
                        }}>
                        Copy Invoice
                      </Dropdown.Item>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {searchedDocuments.length === 0 && (
            <div className='d-flex justify-content-center'>
              <span>{t('document.table.noRecords')}</span>
            </div>
          )}
          {/* <div className='row'>
             <Pagination
               prev
               next
               total={metaData['totalCount']}
               limit={10}
               activePage={activePage}
               onChangePage={setActivePage}
               className='my-2 justify-content-center'
               size='md'
             />
           </div> */}
        </div>
      </div>
    </>
  );
}
export default SearchPortalDocument;
